import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface UploadQueueItem {
  id: string;
  file: File;
  previewUrl: string;
  status: 'pending' | 'uploading' | 'done' | 'failed';

  filename?: string;
  name?: string;
  mime?: string;
  extension?: string;
  width?: string;
  height?: string;
  size?: string;

  url?: string;
  thumbUrl?: string;
  mediumUrl?: string;
  deleteUrl?: string;

  error?: string;
}

@Injectable({ providedIn: 'root' })
export class ImgUploadService {
  private readonly apiUrl = 'https://api.imgbb.com/1/upload';

  private queue: UploadQueueItem[] = [];
  private uploading = false;

  private queueSubject = new BehaviorSubject<UploadQueueItem[]>([]);
  queue$ = this.queueSubject.asObservable();

  addFiles(files: FileList | File[]): void {
    Array.from(files).forEach(file => {
      this.queue.push({
        id: crypto.randomUUID(),
        file,
        previewUrl: URL.createObjectURL(file),
        status: 'pending'
      });
    });

    this.emit();
    this.processQueue();
  }

  remove(id: string): void {
    const item = this.queue.find(upload => upload.id === id);

    if (!item || item.status === 'uploading') return;

    if (item.previewUrl) {
      URL.revokeObjectURL(item.previewUrl);
    }

    this.queue = this.queue.filter(upload => upload.id !== id);
    this.emit();
  }

  getUploadedUrls(): string[] {
    return this.queue
      .filter(item => item.status === 'done' && item.url)
      .map(item => item.url as string);
  }

  clearDone(): void {
    this.queue.forEach(item => {
      if (item.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }
    });

    this.queue = [];
    this.emit();
  }

  private async processQueue(): Promise<void> {
    if (this.uploading) return;

    const nextItem = this.queue.find(item => item.status === 'pending');
    if (!nextItem) return;

    this.uploading = true;
    nextItem.status = 'uploading';
    this.emit();

    try {
      const uploaded = await this.uploadFile(nextItem.file);

      Object.assign(nextItem, uploaded);

      nextItem.status = 'done';
    } catch (error: any) {
      nextItem.status = 'failed';
      nextItem.error = error?.message || 'Upload failed';
    }

    this.uploading = false;
    this.emit();

    this.processQueue();
  }

  private async uploadFile(file: File): Promise<Partial<UploadQueueItem>> {
    const formData = new FormData();

    formData.append('key', environment.imgKey);
    formData.append('image', file);

    const response = await fetch(this.apiUrl, {
      method: 'POST',
      body: formData
    });

    const result = await response.json();

    if (!response.ok || !result?.success) {
      throw new Error(result?.error?.message || 'Img upload failed');
    }

    return {
      filename: result.data.image?.filename,
      name: result.data.image?.name,
      mime: result.data.image?.mime,
      extension: result.data.image?.extension,

      width: result.data.width,
      height: result.data.height,
      size: result.data.size,

      url: result.data.url,
      thumbUrl: result.data.thumb?.url,
      mediumUrl: result.data.medium?.url,
      deleteUrl: result.data.delete_url
    };
  }

  private emit(): void {
    this.queueSubject.next([...this.queue]);
  }
}
